import React, { useMemo, useState } from "react";

import {
  faCalculatorSimple,
  faMinus,
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { HighlightedText } from "src/base-components/HighlightedText";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { Tooltip } from "src/design-system/Tooltip";
import {
  EntityFilter,
  EventTypeFilter,
} from "src/featureCatalogue/Features/FilterActions";
import { StatusToggleRequest } from "src/featureCatalogue/Features/StatusDropdown";
import {
  DisplaySqlCell,
  QUERY_FILTER_CONFIG,
} from "src/featureCatalogue/Queries/Queries";
import { useFeatureQueries } from "src/featureCatalogue/queries";
import { TableActions } from "src/featureCatalogue/TableActions";
import { useFilteredData } from "src/featureCatalogue/useFilteredData";
import {
  commonColumns,
  EntityPills,
  FilterOptions,
  Query,
  statusColumns,
} from "src/featureCatalogue/utils";
import { WhitePane } from "src/jobs/common/WhitePane";
import {
  DashboardPageParams,
  getUrlToManageFeatureQueryPage,
} from "src/router/urls";
import { getTypeIcon } from "src/utils/icons";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { useParamsDecode } from "src/utils/useParamsDecode";

const queryColumnHelper = createColumnHelper<Query>();

const getFeatureColumns = (
  filterOptions: FilterOptions,
  onRequestStatusChange: (request: StatusToggleRequest) => void,
) => [
  queryColumnHelper.accessor(
    (row) =>
      row.columns.features?.map((feature) => feature.feature_key).join(" ") ??
      "",
    {
      id: "feature_key",
      header: (info) => <Header info={info}>Feature Key</Header>,
      cell: (info) => {
        const features = info.row.original.columns.features ?? [];
        return (
          <Cell className="h-auto" info={info}>
            <div className="flex min-h-fit flex-col items-start space-y-2">
              {features.map((feature) => (
                <Tooltip
                  key={feature.feature_key}
                  delay={250}
                  headerAction={
                    <CopyTextIcon
                      variant="inverted"
                      feedback="inline-only-checkmark"
                      value={feature.feature_key}
                    />
                  }
                  placement="top"
                  title={feature.feature_key}
                  asChild
                >
                  <Pill size="sm" variant="gray">
                    <Pill.Icon
                      icon={getTypeIcon(feature.feature_type ?? "", faMinus)}
                    />
                    <Pill.Text>
                      <HighlightedText
                        highlight={info.table.getState().globalFilter}
                        highlightClassName="font-inter-semibold-12px bg-yellow-200"
                        text={feature.feature_key}
                      />
                    </Pill.Text>
                  </Pill>
                </Tooltip>
              ))}
            </div>
          </Cell>
        );
      },
      minSize: 300,
      maxSize: 300,
      filterFn: "includesString",
      enableGlobalFilter: true,
      meta: { sticky: "left", displayName: "Key" },
      enableHiding: false,
    },
  ),
  queryColumnHelper.accessor(
    (row) => row.query_name || "Untitled Feature Query",
    {
      id: "query_name",
      header: (info) => <Header info={info}>Query Name</Header>,
      cell: (info) => <DisplaySqlCell info={info} />,
      minSize: 250,
      maxSize: 250,
      filterFn: "includesString",
      enableGlobalFilter: true,
    },
  ),
  queryColumnHelper.accessor(
    (row) =>
      row.columns.entities?.map((entity) => entity.entity_name).join(" ") ?? "",
    {
      id: "entities",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            <span>Entity</span>
            <EntityFilter
              options={filterOptions.entityTypes}
              property="entities"
            />
          </div>
        </Header>
      ),
      cell: (info) => {
        const entities = info.row.original.columns.entities;
        return (
          <Cell info={info}>
            <EntityPills entities={entities?.map((e) => e.entity_name) ?? []} />
          </Cell>
        );
      },
      minSize: 150,
      maxSize: 150,
      filterFn: "includesString" as const,
      enableGlobalFilter: true,
    },
  ),
  queryColumnHelper.accessor("event", {
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-2">
          <span>Event</span>
          <EventTypeFilter options={filterOptions.eventTypes} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Icon icon={faCalculatorSimple} />
          <Pill.Text>{info.getValue()}</Pill.Text>
        </Pill>
      </Cell>
    ),
    minSize: 200,
    maxSize: 200,
  }),
  ...commonColumns<Query>().filter((column) => column.accessorKey !== "event"),
  ...statusColumns(queryColumnHelper, {
    getEnvironmentSettings: (row) => row?.environment_settings,
    hasQuery: (row) => !!row?.id,
    getQueryId: (row) => row?.id ?? "",
    getEtag: (row) => row?.etag ?? "",
    getEntities: (row) => row.columns.entities?.map((e) => e.entity_name) ?? [],
    getEvent: (row) => row?.event ?? "",
    getFeatures: (row) => row?.columns?.features ?? [],
    onRequestStatusChange,
  }),
  queryColumnHelper.display({
    id: "feature_default",
    header: (info) => (
      <Header info={info}>
        <span className="whitespace-nowrap">Default Value</span>
      </Header>
    ),
    cell: (info) => {
      const features = info.row.original.columns.features ?? [];
      return (
        <Cell className="h-auto" info={info}>
          <div className="flex min-h-fit flex-col items-start space-y-2">
            {features.map((feature) => (
              <span
                key={feature.feature_key}
                className="font-inter-regular-12px"
              >
                {speakPythonPrimitive(feature.feature_default ?? "None")}
              </span>
            ))}
          </div>
        </Cell>
      );
    },
    minSize: 130,
    maxSize: 130,
  }),
  queryColumnHelper.display({
    id: "feature_name",
    header: (info) => <Header info={info}>Feature Name</Header>,
    cell: (info) => {
      const features = info.row.original.columns.features ?? [];
      return (
        <Cell className="h-auto" info={info}>
          <div className="flex min-h-fit flex-col space-y-2">
            {features.map((feature) => (
              <div
                key={feature.feature_key}
                className="flex items-center gap-x-0.5"
              >
                <Tooltip placement="top" title={feature.name ?? ""} asChild>
                  <div className="truncate">
                    <HighlightedText
                      highlight={info.table.getState().globalFilter}
                      highlightClassName="font-inter-semibold-12px bg-yellow-200"
                      text={feature.name ?? ""}
                    />
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>
        </Cell>
      );
    },
    minSize: 200,
    maxSize: 200,
    enableGlobalFilter: false,
  }),
  queryColumnHelper.display({
    id: "feature_description",
    header: (info) => <Header info={info}>Description</Header>,
    cell: (info) => {
      const features = info.row.original.columns.features ?? [];
      return (
        <div className="flex min-h-fit flex-col">
          {features.map((feature) => (
            <DisplayDescriptionCell
              key={feature.feature_key}
              info={info}
              value={feature.description}
            />
          ))}
        </div>
      );
    },
    minSize: 150,
    maxSize: 150,
    enableGlobalFilter: false,
  }),
  queryColumnHelper.display({
    id: "actions-menu",
    header: (info) => (
      <Header info={info}>
        <span />
      </Header>
    ),
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <TableActions query={info.row.original} />
      </Cell>
    ),
    minSize: 120,
    maxSize: 120,
    meta: { sticky: "right" },
  }),
];

export const Features: React.FC<{
  searchQuery: string;
  resetSearch: () => void;
  onRequestStatusChange: (request: StatusToggleRequest) => void;
}> = ({ searchQuery, resetSearch, onRequestStatusChange }) => {
  const {
    data: queriesData,
    isLoading: queriesLoading,
    isSuccess: queriesSuccess,
  } = useFeatureQueries();
  const queries = useMemo(
    () =>
      (queriesData?.filter((item): item is Query => !!item.id) ??
        []) as Query[],
    [queriesData],
  );
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const {
    filteredItems: filteredQueries,
    hasActiveFilters: hasActiveQueryFilters,
    resetFilters: resetQueryFilters,
  } = useFilteredData({
    items: queries ?? [],
    filterConfig: QUERY_FILTER_CONFIG,
  });

  const filterOptions = useMemo(() => {
    const entityTypes = [
      ...new Set(
        queries.flatMap(
          (q) => q.columns.entities?.map((e) => e.entity_name) ?? [],
        ),
      ),
    ];
    const eventTypes = [
      ...new Set(queries.map((q) => q.event).filter((e): e is string => !!e)),
    ];
    return { entityTypes, eventTypes };
  }, [queries]);

  const columns = useMemo(
    () =>
      getFeatureColumns(filterOptions, onRequestStatusChange) as ColumnDef<
        Query,
        unknown
      >[],
    [filterOptions, onRequestStatusChange],
  );

  const showEmptyState =
    !queriesLoading && queriesSuccess && !filteredQueries.length;
  const isFilteredEmptyState =
    showEmptyState && (hasActiveQueryFilters || !!searchQuery);

  const emptyStateNode = (
    <EmptyState
      actionButton={{
        icon: faPlus,
        text: "Create Feature",
        onClick: () => {
          navigate(getUrlToManageFeatureQueryPage(orgId, wsId));
        },
      }}
      dataLoc="features-empty-state"
      description="Features help you detect patterns and analyze behavior across your entities and events."
      headline="No features configured yet"
      icon={faCalculatorSimple}
    />
  );

  const filterEmptyStateNode = (
    <EmptyState
      actionButton={
        hasActiveQueryFilters || searchQuery
          ? {
              text: "Clear filters",
              onClick: () => {
                resetQueryFilters();
                resetSearch();
              },
            }
          : undefined
      }
      description="Try adjusting your search or filters to find what you're looking for"
      headline="No matching features found"
      icon={faSearch}
    />
  );

  return (
    <>
      <WhitePane>
        <div className="flex h-full w-full flex-col gap-y-4">
          {queriesSuccess && queries.length === 0 ? (
            emptyStateNode
          ) : (
            <div
              className={twJoin(
                "relative flex min-h-0 flex-1 flex-col p-4",
                showEmptyState && "items-center justify-center",
              )}
            >
              <TableComp
                columns={columns}
                data={filteredQueries}
                dataLoc="features-list"
                emptyState={filteredQueries.length > 0 && filterEmptyStateNode}
                frameClassName="w-full min-h-0 flex-1"
                globalFilter={searchQuery}
                initialState={{
                  sorting: [
                    { id: "window_length", desc: false },
                    { id: "window_hop", desc: false },
                  ],
                }}
                isLoading={queriesLoading}
                rowPropsGetter={(row) => {
                  const query = row.original;
                  return {
                    "data-loc": query.columns.features?.[0]?.feature_key,
                    onClick: () => {
                      navigate(
                        getUrlToManageFeatureQueryPage(
                          orgId,
                          wsId,
                          query.id,
                          "view",
                        ),
                      );
                    },
                    classNameOverrides:
                      "cursor-pointer hover:bg-gray-50 [&>td.sticky]:z-10 [&>td.sticky]:bg-white [&>td]:align-top [&_.sticky]:hover:bg-gray-50",
                  };
                }}
                variant="compact"
                clientSideFiltering
              />
              {showEmptyState &&
                (isFilteredEmptyState ? filterEmptyStateNode : emptyStateNode)}
            </div>
          )}
        </div>
      </WhitePane>
    </>
  );
};

const getFirstLineContent = (descriptionValue: string) => {
  if (descriptionValue.includes("\n")) {
    return `${descriptionValue.split("\n")[0]}...`;
  }
  return descriptionValue.length > 50
    ? `${descriptionValue.slice(0, 43)}...`
    : descriptionValue;
};

export const DisplayDescriptionCell = <D extends object, V>({
  info,
  value: descriptionValue,
}: {
  info: CellContext<D, V>;
  value: string | null | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const needsPopover =
    descriptionValue &&
    (descriptionValue.length > 50 || descriptionValue.includes("\n"));

  const firstLineContent = getFirstLineContent(descriptionValue ?? "");

  const content = (
    <div className="line-clamp-1 overflow-hidden">
      <MarkdownRenderer
        content={needsPopover ? firstLineContent : (descriptionValue ?? "")}
      />
    </div>
  );

  return needsPopover ? (
    <CustomPopover
      button={<Cell info={info}>{content}</Cell>}
      className="w-96 overflow-auto border-0"
      isOpen={isOpen}
      offsetX={0}
      placement="top"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {isOpen && descriptionValue && (
        <div className="p-3">
          <MarkdownRenderer content={descriptionValue} />
        </div>
      )}
    </CustomPopover>
  ) : (
    <Cell info={info}>{content}</Cell>
  );
};
